import React, { useState } from 'react';

interface ApiResponse {
  status: number;
  statusText: string;
  data: any;
  error?: string;
}

interface TestResult {
  loading: boolean;
  response?: ApiResponse;
  error?: string;
}

const ApiTest: React.FC = () => {
  const [results, setResults] = useState<Record<string, TestResult>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // 基础URL配置
  const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api/v2';

  // 通用请求函数
  const makeRequest = async (url: string): Promise<ApiResponse> => {
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      return {
        status: response.status,
        statusText: response.statusText,
        data,
      };
    } catch (error: any) {
      return {
        status: 0,
        statusText: 'Network Error',
        data: null,
        error: error.message,
      };
    }
  };

  // 显示响应结果
  const showResponse = (testId: string, result: ApiResponse) => {
    setResults((prev) => ({
      ...prev,
      [testId]: {
        loading: false,
        response: result,
      },
    }));
  };

  // 显示加载状态
  const showLoading = (testId: string) => {
    setResults((prev) => ({
      ...prev,
      [testId]: {
        loading: true,
      },
    }));
  };

  // 测试函数
  const testHealth = async () => {
    showLoading('health');
    const result = await makeRequest(`${BASE_URL}/health`);
    showResponse('health', result);
  };

  const testMarketData = async () => {
    showLoading('market');
    const result = await makeRequest(`${BASE_URL}/market/tickers`);
    showResponse('market', result);
  };

  const testAccountBalance = async () => {
    showLoading('balance');
    const result = await makeRequest(`${BASE_URL}/account/balance`);
    showResponse('balance', result);
  };

  const testPositions = async (instType: string) => {
    showLoading('positions');
    const result = await makeRequest(`${BASE_URL}/account/positions?instType=${instType}`);
    showResponse('positions', result);
  };

  const testOrders = async (instType: string, state: string, limit: string) => {
    showLoading('orders');
    const result = await makeRequest(`${BASE_URL}/account/orders?instType=${instType}&state=${state}&limit=${limit}`);
    showResponse('orders', result);
  };

  const testPositionsHistory = async (instType: string, days: string) => {
    showLoading('positions-history');
    const result = await makeRequest(`${BASE_URL}/account/positions-history?instType=${instType}&days=${days}`);
    showResponse('positions-history', result);
  };

  const testPnL = async (instType: string, days: string) => {
    showLoading('pnl');
    const result = await makeRequest(`${BASE_URL}/account/pnl?instType=${instType}&days=${days}`);
    showResponse('pnl', result);
  };

  const testDailyProfits = async (userId: string, startDate: string, endDate: string) => {
    showLoading('daily-profits');
    const result = await makeRequest(
      `${BASE_URL}/daily-profits?userId=${userId}&startDate=${startDate}&endDate=${endDate}`,
    );
    showResponse('daily-profits', result);
  };

  const testCacheStats = async () => {
    showLoading('cache-stats');
    const result = await makeRequest(`${BASE_URL}/system/cache-stats`);
    showResponse('cache-stats', result);
  };

  const testSystemMetrics = async () => {
    showLoading('system-metrics');
    const result = await makeRequest(`${BASE_URL}/system/metrics`);
    showResponse('system-metrics', result);
  };

  const testAppMetrics = async () => {
    showLoading('app-metrics');
    const result = await makeRequest(`${BASE_URL}/system/app-metrics`);
    showResponse('app-metrics', result);
  };

  // 复制响应结果到剪贴板
  const copyResponse = async (testId: string) => {
    const result = results[testId];
    if (!result?.response) return;

    const { status, statusText, data, error } = result.response;
    const responseText = error
      ? `错误: ${error}`
      : JSON.stringify(
          {
            status,
            statusText,
            data,
          },
          null,
          2,
        );

    try {
      await navigator.clipboard.writeText(responseText);
      setCopiedId(testId);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  // 渲染响应结果
  const renderResponse = (testId: string) => {
    const result = results[testId];
    if (!result) return null;

    if (result.loading) {
      return (
        <div className="p-4 bg-blue-50 border-l-4 border-blue-400 rounded">
          <h4 className="text-blue-600 font-semibold">状态: 请求中...</h4>
          <p className="text-blue-600">正在发送请求，请稍候...</p>
        </div>
      );
    }

    if (result.response) {
      const { status, statusText, data, error } = result.response;
      const isSuccess = status >= 200 && status < 300;

      return (
        <div
          className={`p-4 border-l-4 rounded ${
            isSuccess ? 'bg-green-50 border-green-400' : 'bg-red-50 border-red-400'
          }`}
        >
          <div className="flex justify-between items-center mb-2">
            <h4 className={`font-semibold ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
              响应状态: {status} {statusText}
            </h4>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">{new Date().toLocaleTimeString()}</span>
              <button
                onClick={() => copyResponse(testId)}
                className={`px-3 py-1 text-xs rounded transition-colors ${
                  copiedId === testId ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                title="复制响应结果"
              >
                {copiedId === testId ? '已复制 ✓' : '复制'}
              </button>
            </div>
          </div>
          <pre className="bg-white p-3 rounded text-xs overflow-x-auto mt-2">
            {error ? `错误: ${error}` : JSON.stringify(data, null, 2)}
          </pre>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 overflow-x-hidden">
      <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg overflow-hidden w-full max-w-full">
        {/* Header */}
        <div className="bg-blue-600 text-white p-6 text-center">
          <h1 className="text-2xl font-bold mb-2">🔧 API接口测试页面 (v2 API)</h1>
          <p className="text-blue-100">测试后端 v2 API 接口功能 (Fastify + Prisma + Redis)</p>
        </div>

        <div className="p-6 space-y-6">
          {/* 健康检查 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">🏥 健康检查</h3>
              <button
                onClick={testHealth}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">检查后端服务状态</p>
              {renderResponse('health')}
            </div>
          </div>

          {/* 市场数据 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📊 市场数据</h3>
              <button
                onClick={testMarketData}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取热门币种市场数据</p>
              {renderResponse('market')}
            </div>
          </div>

          {/* 账户余额 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">💰 账户余额</h3>
              <button
                onClick={testAccountBalance}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取OKX账户余额信息</p>
              {renderResponse('balance')}
            </div>
          </div>

          {/* 持仓信息 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📈 持仓信息</h3>
              <button
                onClick={() => testPositions('SWAP')}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取当前持仓信息</p>
              {renderResponse('positions')}
            </div>
          </div>

          {/* 交易历史 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📋 交易历史</h3>
              <button
                onClick={() => testOrders('SWAP', 'filled', '20')}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取交易历史记录</p>
              {renderResponse('orders')}
            </div>
          </div>

          {/* 历史持仓信息 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📊 历史持仓信息</h3>
              <button
                onClick={() => testPositionsHistory('SWAP', '30')}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取历史持仓信息</p>
              {renderResponse('positions-history')}
            </div>
          </div>

          {/* 盈亏历史 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📊 盈亏历史</h3>
              <button
                onClick={() => testPnL('SWAP', '30')}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取盈亏历史记录</p>
              {renderResponse('pnl')}
            </div>
          </div>

          {/* 每日收益 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📈 每日收益</h3>
              <button
                onClick={() => testDailyProfits('1', '', '')}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取每日收益数据</p>
              {renderResponse('daily-profits')}
            </div>
          </div>

          {/* 缓存统计 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">🗄️ 缓存统计</h3>
              <button
                onClick={testCacheStats}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取 Redis 缓存统计信息</p>
              {renderResponse('cache-stats')}
            </div>
          </div>

          {/* 系统指标 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📊 系统指标</h3>
              <button
                onClick={testSystemMetrics}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取系统性能指标</p>
              {renderResponse('system-metrics')}
            </div>
          </div>

          {/* 应用指标 */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 flex justify-between items-center border-b">
              <h3 className="font-semibold text-gray-800">📈 应用指标</h3>
              <button
                onClick={testAppMetrics}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors"
              >
                测试
              </button>
            </div>
            <div className="p-4">
              <p className="text-gray-600 mb-4">获取应用性能指标</p>
              {renderResponse('app-metrics')}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiTest;
